import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SelectOutlined
} from "@ant-design/icons";
import { Button, Input, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

export interface CrudSearch {
    placeholder: string;
    /** Current term, used as the field's initial value. */
    value?: string;
    onChange: (value: string) => void;
    /** Runs the query — these lists search on the server. */
    onSearch: () => void;
}

interface CrudToolbarProps {
    /** A row is selected, so Delete is meaningful. */
    hasSelection: boolean;
    /** The selection is a persisted record (has an id), so Edit is meaningful. */
    canEdit: boolean;
    /** Name of the selected record, shown in the contextual strip. */
    selectionLabel?: string | null;
    onDelete: () => void;
    onEdit: () => void;
    /** Omitted where the entity cannot be created from this list. */
    onAdd?: () => void;
    onReload: () => void;
    /** Drops the selection without touching the record. */
    onClearSelection?: () => void;
    /** Server-side search, rendered on the leading edge of the bar. */
    search?: CrudSearch;
}

/**
 * The action bar above every entity list.
 *
 * It has two states rather than one. Idle, it offers only what is always
 * available — refresh and add — and says how to get the rest. With a row
 * selected it becomes a contextual strip naming that row, so a destructive
 * button is never sitting on screen greyed out next to an invisible selection.
 */
export const CrudToolbar = ({
    hasSelection,
    canEdit,
    selectionLabel,
    onDelete,
    onEdit,
    onAdd,
    onReload,
    onClearSelection,
    search
}: CrudToolbarProps) => {
    const { t } = useTranslation();

    return (
        <div className={`crud-bar${hasSelection ? " is-selected" : ""}`}>
            {hasSelection ? (
                <div className="crud-bar__selection">
                    <span className="crud-bar__dot" />
                    <span>{t("list.selected")}</span>
                    <span className="crud-bar__selection-name">{selectionLabel}</span>
                </div>
            ) : search != null ? (
                <Input.Search
                    className="crud-bar__search"
                    allowClear
                    enterButton
                    placeholder={search.placeholder}
                    defaultValue={search.value}
                    onChange={event => search.onChange(event.target.value)}
                    onSearch={search.onSearch}
                />
            ) : (
                <div className="crud-bar__hint">
                    <SelectOutlined />
                    <span>{t("list.selectHint")}</span>
                </div>
            )}

            <div className="crud-bar__spacer" />

            <div className="crud-bar__actions">
                {hasSelection ? (
                    <>
                        {onClearSelection != null ? (
                            <Button type="text" onClick={onClearSelection}>
                                {t("actions.clearSelection")}
                            </Button>
                        ) : null}
                        <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
                            {t("actions.delete")}
                        </Button>
                        {canEdit ? (
                            <Button type="primary" ghost icon={<EditOutlined />} onClick={onEdit}>
                                {t("actions.edit")}
                            </Button>
                        ) : null}
                        <span className="crud-bar__divider" />
                    </>
                ) : null}

                <Tooltip title={t("actions.refresh")}>
                    <Button
                        type="text"
                        shape="circle"
                        aria-label={t("actions.refresh")}
                        icon={<ReloadOutlined />}
                        onClick={onReload}
                    />
                </Tooltip>
                {onAdd != null ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
                        {t("actions.add")}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
